import { type ReactNode, useState, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { NOTIFICATION_TYPE_REGISTRY } from '@resida/notification-types';
import { CookiePreferencesLink } from '@resida/analytics';
import { LANDLORD_POLICY_LINKS } from '@resida/consent-ui';
import type { DocumentPolicyType } from '@resida/shared';
import { countActNowItems, countItemsBySeverity } from '@resida/api-types/attention';
import { NotificationBell } from '../NotificationBell';
import { BillingBanner } from '../BillingBanner';
import { PolicyModal } from '../PolicyModal';
import { BottomTabBar } from './BottomTabBar';
import { MoreMenu } from './MoreMenu';
import { Sidebar } from './Sidebar';
import { TopBar } from './TopBar';
import { SupportChatWidget } from '../support/SupportChatWidget';
import { expensesApi, listConversations, getAttentionItems } from '../../lib/api';
import { fetchInboxUnrespondedCount, fetchUnreviewedApplicationsCount } from '../../lib/leasing.api';
import { fetchUnmatchedTransactionsCount } from '../../api/external-payments-hub.api';
import { useDomainEvents } from '../../hooks/useDomainEvents';
import { useUnseenCounts } from '../../hooks/useAwareness';
import { useNotificationDeepLink } from '../../hooks/useNotificationDeepLink';
import { useBillingFeatures } from '../../hooks/useBilling';
import { Workflow } from 'lucide-react';
import { WorkflowPanelProvider } from '../workflow-panel/WorkflowPanelProvider';
import { useWorkflowPanel } from '../workflow-panel/useWorkflowPanel';
import { WorkflowRail } from '../workflow-panel/WorkflowRail';
import { WorkflowPanelErrorBoundary } from '../workflow-panel/WorkflowPanelErrorBoundary';
import { HelpButton } from '../help/HelpButton';
import { HelpPanel } from '../help/HelpPanel';
import { useHelp } from '../help/HelpProvider';

/** Desktop-only footer (hidden below md), so these text links may be denser than 44px. */
const FOOTER_LINK_CLASS = 'hover:text-foreground transition-colors underline-offset-2 hover:underline';

/** DC-4 — sum of byClass unseen counts across every leasing_pipeline registry class, so the
 *  leasing unseen dot doesn't need one hardcoded class name per proposal-response type. */
const LEASING_PIPELINE_CLASSES = Object.values(NOTIFICATION_TYPE_REGISTRY)
  .filter((def) => def.category === 'leasing_pipeline')
  .map((def) => def.type);

interface AppShellProps {
  children: ReactNode;
}

/** Desktop-only workflow toggle in the header (lg+ only, where the rail exists). */
function DesktopWorkflowButton() {
  const { workflows, isLoading, isRailExpanded, toggleRail } = useWorkflowPanel();
  const activeCount = workflows.filter((w) => w.status !== 'dismissed').length;

  return (
    <button
      type="button"
      data-testid="workflow-rail-toggle"
      onClick={toggleRail}
      className="hidden lg:flex relative items-center justify-center w-9 h-9 rounded-md text-muted-foreground hover:bg-accent/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label={`${isRailExpanded ? 'Collapse' : 'Expand'} workflow panel${activeCount > 0 ? ` — ${activeCount} active` : ''}`}
      aria-expanded={isRailExpanded}
      aria-controls="workflow-rail"
    >
      <Workflow className="h-4 w-4" aria-hidden="true" />
      {!isLoading && activeCount > 0 && (
        <span
          data-testid="workflow-rail-badge"
          className="absolute top-0.5 right-0.5 flex items-center justify-center min-w-3.5 h-3.5 px-0.5 text-[10px] font-bold bg-primary text-primary-foreground rounded-full"
          aria-label={`${activeCount} active workflow${activeCount !== 1 ? 's' : ''}`}
        >
          {activeCount > 9 ? '9+' : activeCount}
        </span>
      )}
    </button>
  );
}

export function AppShell({ children }: AppShellProps) {
  const [moreOpen, setMoreOpen] = useState(false);
  const [openPolicy, setOpenPolicy] = useState<DocumentPolicyType | null>(null);

  const queryClient = useQueryClient();
  const { data: featuresData } = useBillingFeatures();
  const { open: helpOpen } = useHelp();

  useNotificationDeepLink();

  // BR-15/IAM-BR-RT2 — chat badge is a server total, correct on cold load. The event only
  // invalidates; nothing is ever accumulated client-side (no useState(0) + prev+1).
  const { data: conversationsData } = useQuery({
    queryKey: ['chat-conversations-summary'],
    queryFn: () => listConversations(),
    staleTime: 30_000,
    retry: false,
    throwOnError: false,
  });
  const chatUnreadCount = (conversationsData?.conversations ?? []).reduce(
    (sum, c) => sum + (c.unreadCount ?? 0),
    0
  );

  // BR-15/ET-BR-44 — expense badge is a server count; no more window CustomEvent fan-out.
  const { data: reviewQueueData } = useQuery({
    queryKey: ['expense-review-queue-count'],
    queryFn: () => expensesApi.getReviewQueue(),
    staleTime: 30_000,
    retry: false,
    throwOnError: false,
  });
  const reviewCount = reviewQueueData?.expenses.length ?? 0;

  // Fetch inbox unresponded count for leasing nav badge — React Query with staleTime
  const { data: inboxCountData } = useQuery({
    queryKey: ['inbox-unresponded-count'],
    queryFn: () => fetchInboxUnrespondedCount(),
    staleTime: 60_000,
    // Silent fail — badge is informational
    retry: false,
    throwOnError: false,
  });
  const inboxCount = inboxCountData?.count ?? 0;

  // BR-38 — Applications-tab count (submitted-and-unreviewed applicants), fetched
  // alongside the existing inbox-unresponded count. Shares its query key with
  // LeasingLayout.tsx's Applications-tab badge so TanStack dedupes the fetch.
  const { data: unreviewedApplicationsData } = useQuery({
    queryKey: ['applications-unreviewed-count'],
    queryFn: () => fetchUnreviewedApplicationsCount(),
    staleTime: 60_000,
    // Silent fail — badge is informational
    retry: false,
    throwOnError: false,
  });
  const unreviewedApplicationsCount = unreviewedApplicationsData?.count ?? 0;

  // BR-23 — nav "Attention" badge is a projection of the same attention-engine item set the
  // dashboard bands render, never a separately computed count. Per the spec-slice, this is the
  // `act_now`-band count specifically (the items that need action right now), not
  // `attentionSummary.visible` (which would include waiting/upcoming/backlog too). Shares its
  // react-query key with AttentionPage.tsx's own dismiss/snooze/undo invalidation so this badge
  // updates promptly when a manager acts from that page, not just after `staleTime` elapses.
  const { data: attentionData } = useQuery({
    queryKey: ['attention-items-summary'],
    queryFn: () => getAttentionItems(),
    staleTime: 30_000,
    retry: false,
    throwOnError: false,
  });
  const attentionCount = countActNowItems(attentionData?.items ?? []);
  // Required / recommended counts sit beside the act_now count in the nav (same item set, so
  // BR-23's "never independently computed" still holds).
  const attentionRequiredCount = countItemsBySeverity(attentionData?.items ?? [], 'required');
  const attentionRecommendedCount = countItemsBySeverity(attentionData?.items ?? [], 'recommended');

  // R-EXT-PAY-BADGE — External Payments nav badge is a section badge per ADR-AT-009,
  // computed from external-payments' own data (unmatched incoming transaction count),
  // never derived from AttentionItems.
  const { data: unmatchedTransactionsCountData } = useQuery({
    queryKey: ['external-payments-unmatched-count'],
    queryFn: () => fetchUnmatchedTransactionsCount(),
    staleTime: 60_000,
    // Silent fail — badge is informational
    retry: false,
    throwOnError: false,
  });
  const externalPaymentsUnmatchedCount = unmatchedTransactionsCountData?.count ?? 0;

  const invalidateChatBadge = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['chat-conversations-summary'] });
  }, [queryClient]);
  const invalidateExpenseBadge = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['expense-review-queue-count'] });
  }, [queryClient]);
  // BR-38 — the Leasing badge is now a hierarchy (top-level = Leads/Inbox count +
  // Applications count, non-double-counting), so any leasing-relevant event refreshes
  // BOTH underlying counts rather than just the Leads/Inbox one.
  const invalidateLeasingBadge = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['inbox-unresponded-count'] });
    void queryClient.invalidateQueries({ queryKey: ['applications-unreviewed-count'] });
  }, [queryClient]);
  // BR-27 — the Attention badge (and every consumer sharing this query key, e.g.
  // AttentionPage.tsx) re-reads whenever an attention-relevant domain event arrives, rather
  // than waiting out `staleTime`. Chat/Expense/Leasing badges themselves stay their own
  // separately-computed counts (ADR-AT-009) — this only invalidates the attention-items query.
  const invalidateAttentionBadge = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['attention-items-summary'] });
  }, [queryClient]);
  const invalidateExpenseAndAttentionBadges = useCallback(() => {
    invalidateExpenseBadge();
    invalidateAttentionBadge();
  }, [invalidateExpenseBadge, invalidateAttentionBadge]);
  // R-EXT-PAY-BADGE — the External Payments nav badge is its own section badge (ADR-AT-009),
  // not a projection of AttentionItems, so it needs its own invalidation callback.
  const invalidateExternalPaymentsBadge = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['external-payments-unmatched-count'] });
  }, [queryClient]);
  // R-EXT-PAY-BADGE — bank_transaction.status_changed / linked_bank_account.status_changed
  // already invalidate the Attention badge (both are attention providers); they also now
  // invalidate the External Payments section badge since either event can move an unmatched
  // transaction in or out of the unmatched set.
  const invalidateExternalPaymentsAndAttentionBadges = useCallback(() => {
    invalidateExternalPaymentsBadge();
    invalidateAttentionBadge();
  }, [invalidateExternalPaymentsBadge, invalidateAttentionBadge]);
  // leasing-support/BR-38, BR-AT-18 — a new lead, an outbound reply, or an applicant being
  // marked reviewed all move both the leasing badge hierarchy AND the attention surface
  // (folded leasing-pipeline items), so each refreshes both rather than only one.
  const invalidateLeasingAndAttentionBadges = useCallback(() => {
    invalidateLeasingBadge();
    invalidateAttentionBadge();
  }, [invalidateLeasingBadge, invalidateAttentionBadge]);

  // listing-autofill-extension — the external-post takedown attention item and the listing's
  // "Posted elsewhere" list both follow these two events.
  const invalidateExternalPostsAndAttention = useCallback(
    (event: { listingId: string }) => {
      invalidateAttentionBadge();
      void queryClient.invalidateQueries({ queryKey: ['listing', event.listingId, 'external-posts'] });
    },
    [queryClient, invalidateAttentionBadge],
  );

  // LS-BR-NOTIF-14/BR-7 — the leasing badge subscribes to all EIGHT leasing DomainEvent arms,
  // not the three the old usePersonalEvents surface offered — plus case.message_received
  // (JR-1): a new inbound lead/prospect message is exactly what puts a lead back into the
  // "unresponded" count, and without this key the badge only caught up after staleTime (60s)
  // or a WebSocket reconnect, never live.
  useDomainEvents({
    new_chat_message: invalidateChatBadge,
    // BR-27 — 'expense.review_queued' already invalidated the expense badge; it also now
    // invalidates the attention badge since a newly-queued expense is an attention item too.
    'expense.review_queued': invalidateExpenseAndAttentionBadges,
    'case.message_received': invalidateLeasingBadge,
    'lead.decided': invalidateLeasingBadge,
    'appointment_proposal.created': invalidateLeasingBadge,
    'appointment_proposal.confirmed': invalidateLeasingBadge,
    'appointment_proposal.rejected': invalidateLeasingBadge,
    'appointment_proposal.cancelled': invalidateLeasingBadge,
    'appointment_proposal.rescheduled': invalidateLeasingBadge,
    'showing.scheduled': invalidateLeasingBadge,
    'applicant.submitted': invalidateLeasingBadge,
    // leasing-support/BR-38, BR-AT-18 — folding leasing-pipeline items into the
    // Obligations/attention surface + the Applications-tab badge means these three now
    // move both the leasing badge hierarchy and the attention surface.
    'lead.created': invalidateLeasingAndAttentionBadges,
    'case.message_sent': invalidateLeasingAndAttentionBadges,
    'applicant.reviewed': invalidateLeasingAndAttentionBadges,
    'ledger.entry_posted': invalidateAttentionBadge,
    'pending_late_fee.status_changed': invalidateAttentionBadge,
    // R-EXT-PAY-BADGE — these two also drive the External Payments section badge now,
    // in addition to the Attention badge they already refreshed.
    'bank_transaction.status_changed': invalidateExternalPaymentsAndAttentionBadges,
    'linked_bank_account.status_changed': invalidateExternalPaymentsAndAttentionBadges,
    'signing_envelope.status_changed': invalidateAttentionBadge,
    'chat.message_sent': invalidateAttentionBadge,
    'chat.conversation_read': invalidateAttentionBadge,
    'email.thread_read': invalidateAttentionBadge,
    'email.thread_changed': invalidateAttentionBadge,
    'expense.review_resolved': invalidateAttentionBadge,
    'email.message_received': invalidateAttentionBadge,
    // ADR-AT-017 — extend the same live-freshness coverage to every existing attention
    // provider, not just the ones added alongside this badge originally.
    'lease.changed': invalidateAttentionBadge,
    'unit.changed': invalidateAttentionBadge,
    'property.changed': invalidateAttentionBadge,
    'property_bank_account.linked': invalidateAttentionBadge,
    'stripe_connect_account.status_changed': invalidateAttentionBadge,
    'funding_source.status_changed': invalidateAttentionBadge,
    'email_ingest_address.created': invalidateAttentionBadge,
    'manager_credential.changed': invalidateAttentionBadge,
    'manager_profile.changed': invalidateAttentionBadge,
    'tenant_invite.status_changed': invalidateAttentionBadge,
    'listing.status_changed': invalidateExternalPostsAndAttention,
    'external_listing_post.changed': invalidateExternalPostsAndAttention,
  });

  // DC-4/BR-16 (docs/features/change-awareness/specification.md) — ambient-tier unseen
  // counts. BR-14's own text is "mark it new on the thing itself" — a portfolio-wide
  // header/menu dot with no entity context doesn't satisfy that, so these no longer render
  // as a standalone nav-level indicator (see AgentResponseQueuePage/AgentEscalationQueuePage/
  // ExpenseReviewQueuePage/LeadsIndexPage for the per-instance marks that do). This SUM is
  // kept only to drive the mobile "More" tab's ambient-activity dot below — that one's
  // context is "additional navigation," not a specific entity, so an aggregate is the right
  // shape for it.
  const { data: unseenCountsData } = useUnseenCounts();
  const byClass = unseenCountsData?.byClass ?? {};
  const hasLeasingPipeline = featuresData?.features.includes('leasing_pipeline') ?? false;
  const hasAgenticSupport = featuresData?.features.includes('agentic_support') ?? false;
  const mobileUnseenTotal =
    (hasLeasingPipeline
      ? LEASING_PIPELINE_CLASSES.reduce((sum, type) => sum + (byClass[type] ?? 0), 0)
      : 0) +
    (hasAgenticSupport
      ? (byClass[NOTIFICATION_TYPE_REGISTRY.agent_draft_held_for_review.type] ?? 0) +
        (byClass[NOTIFICATION_TYPE_REGISTRY.agent_landlord_escalation.type] ?? 0)
      : 0) +
    (byClass[NOTIFICATION_TYPE_REGISTRY.expense_review_queued.type] ?? 0);

  const badges = {
    expenses: reviewCount,
    reviewCount,
    // BR-7 — Leads/Inbox-tab count, unchanged meaning.
    leasingUnrespondedCount: inboxCount,
    // BR-38 — Applications-tab count.
    leasingUnreviewedApplicationsCount: unreviewedApplicationsCount,
    // BR-38 — top-level Leasing nav badge: non-double-counting sum of the two above.
    leasingTotalCount: inboxCount + unreviewedApplicationsCount,
    chatUnreadCount,
    attentionCount,
    attentionRequiredCount,
    attentionRecommendedCount,
    // R-EXT-PAY-BADGE — External Payments nav badge (section badge, ADR-AT-009).
    externalPaymentsUnmatchedCount,
  };

  return (
    <WorkflowPanelProvider>
    <div className="min-h-[calc(100vh-var(--cookie-consent-height,0px))] flex flex-col">
      {/* Desktop sidebar */}
      <Sidebar badges={badges} />

      {/* Mobile top bar */}
      <TopBar />

      {/* Desktop header with the Help button, notification bell and workflow toggle. Ambient
          unseen-change counts render on their own list-page rows instead of as unlabeled pills here. */}
      <header className="hidden md:flex items-center justify-end gap-1 h-14 px-6 border-b border-border ml-[var(--sidebar-width)]">
        <DesktopWorkflowButton />
        <HelpButton data-testid="help-button" />
        <NotificationBell />
      </header>

      {/* Billing banner */}
      <div className="md:ml-[var(--sidebar-width)] lg:pr-[var(--workflow-rail-width,3rem)] transition-[padding-right] duration-300 ease-in-out">
        <BillingBanner />
      </div>

      {/* Main content */}
      {/*
        On desktop (lg+) the WorkflowRail occupies the right edge of the viewport.
        It sets --workflow-rail-width (3rem collapsed, 20rem expanded) on <html> via
        a useEffect. We read it here so the main content area never sits under the
        fixed rail. The padding-right transitions in sync with the rail animation.
      */}
      <main
        className={
          // Mobile: pad bottom for tab bar, contain overflow
          'pb-[calc(var(--bottom-tab-height)+env(safe-area-inset-bottom,0px))] md:pb-0 ' +
          // Desktop: offset for sidebar
          'md:ml-[var(--sidebar-width)] ' +
          // Desktop: offset right for workflow rail (collapsed 3rem, expanded 20rem)
          // Tailwind cannot use the dynamic CSS var directly in transition, so we
          // apply it as an inline-ish style via a standard Tailwind utility that
          // forwards to the var. The var is updated by WorkflowRail's useEffect.
          'lg:pr-[var(--workflow-rail-width,3rem)] ' +
          // Smooth transition matching the rail's duration-300
          'transition-[padding-right] duration-300 ease-in-out ' +
          // Prevent horizontal overflow on small screens
          'overflow-x-hidden ' +
          // Grow to fill available space so footer stays at viewport bottom
          'flex-1'
        }
      >
        {children}
      </main>

      {/* Footer */}
      <footer
        className="md:ml-[var(--sidebar-width)] lg:pr-[var(--workflow-rail-width,3rem)] transition-[padding-right] duration-300 ease-in-out hidden md:flex flex-wrap items-center justify-center gap-4 py-6 px-4 text-xs text-muted-foreground border-t border-border"
        data-testid="landlord-footer"
      >
        {LANDLORD_POLICY_LINKS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setOpenPolicy(key)}
            className={FOOTER_LINK_CLASS}
            data-testid={`footer-link-${key}`}
            type="button"
          >
            {label}
          </button>
        ))}
        <CookiePreferencesLink className={FOOTER_LINK_CLASS} />
        {openPolicy && (
          <PolicyModal
            policyType={openPolicy}
            isOpen={true}
            onClose={() => setOpenPolicy(null)}
          />
        )}
      </footer>

      {/* Mobile bottom tab bar */}
      <BottomTabBar
        onMoreClick={() => setMoreOpen(true)}
        badges={badges}
        moreUnseenCount={mobileUnseenTotal}
      />

      <MoreMenu
        open={moreOpen}
        onClose={() => setMoreOpen(false)}
        badges={badges}
      />

      {/* Floating support chat widget — hidden while the help panel is open */}
      {!helpOpen && <SupportChatWidget />}

      {/* Help panel — docked on desktop, bottom sheet below 1024px */}
      <HelpPanel />

      {/* Workflow panel — desktop rail only (mobile entry is in TopBar). The docked help panel
          replaces the rail while it is open. */}
      {/* Error boundary ensures a panel crash never takes down AppShell */}
      {!helpOpen && (
        <WorkflowPanelErrorBoundary>
          <WorkflowRail />
        </WorkflowPanelErrorBoundary>
      )}
    </div>
    </WorkflowPanelProvider>
  );
}
